import React, { useMemo } from 'react';
import theme from './OptionGroupAccordion.module.scss';
import { RoomDto, RoomOptionGroupDto } from '@/types';
import { useRoomMap } from '@/services/rooms';
import { RoomPill } from '../Rooms';
import { getPillVariant } from './OptionGroupAccordion.helpers';
import { useQuery } from '@tanstack/react-query';
import { getPricingFields } from '@/services/projects';

export interface OptionGroupRoomsListProps {
    optionGroup: RoomOptionGroupDto;
}

export const OptionGroupRoomsList: React.FC<OptionGroupRoomsListProps> = ({ optionGroup }) => {
    const roomMap = useRoomMap(optionGroup.projectId);
    const { data: pricingFields } = useQuery({
        queryKey: ['pricingFields', optionGroup.projectId],
        queryFn: () => getPricingFields(optionGroup.projectId),
    });

    const pricingFieldNameMap = useMemo(() => {
        const map = new Map<number, string>();
        (pricingFields ?? []).forEach((pf) => map.set(pf.id, pf.name));
        return map;
    }, [pricingFields]);

    const data = useMemo(() => {
        if (!roomMap) {
            return [];
        }

        return optionGroup.linkedRooms
            .map((rl) => ({ room: roomMap.get(rl.roomId), link: rl }))
            .filter((x): x is { room: RoomDto; link: typeof x.link } => x.room !== undefined);
    }, [roomMap, optionGroup]);

    return (
        <div className={theme.roomsList}>
            {data.map((item) => (
                <RoomPill
                    room={item.room}
                    key={item.room.id}
                    variant={getPillVariant(item.link.status)}
                    pricingFieldName={
                        item.link.pricingFieldId ? pricingFieldNameMap.get(item.link.pricingFieldId) : undefined
                    }
                />
            ))}
        </div>
    );
};
